import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Star, Utensils, Users, Clock, ExternalLink, PenLine, X, ChevronLeft, Search } from 'lucide-react';
import { restaurantInfo, welcomeTexts, images, menuCategories } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [currentWelcome, setCurrentWelcome] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: '4,9', count: 198 });
  const { t } = useLanguage();

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWelcome((prev) => (prev + 1) % welcomeTexts.length);
        setIsAnimating(false);
      }, 500);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Fetch Google reviews data
  useEffect(() => {
    fetch(`${API_URL}/api/google-reviews`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setReviewData({ rating: data.rating, count: data.count }); })
      .catch(() => {});
  }, []);

  // Gallery images from our dish photos
  const galleryItems = [
    { src: images.hero, name: 'Assortiment de mezzés', price: null },
    { src: images.hummus, name: 'Houmous', price: '13.00' },
    { src: images.mixGrill, name: 'Grill Mixte', price: '39.00' },
    { src: images.tabouli, name: 'Tabboulé', price: '14.00' },
    { src: images.kibbeh, name: 'Kebbé', price: '12.00' },
    { src: images.kabsaAgneau, name: 'Kabsa', price: '27.00' },
    { src: images.falafel, name: 'Falafels', price: '10.00' },
    { src: images.baklava, name: 'Baklava', price: '5.00' },
    { src: images.moutabbal, name: 'Moutabbal', price: '13.00' },
    { src: images.shawarma, name: 'Araïs à la viande', price: '16.00' },
    { src: images.fattouche, name: 'Fattouche', price: '15.00' },
    { src: images.dorade, name: 'Dorade', price: null },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source src="/images/restaurant_bg.mp4" type="video/mp4" />
        </video>

        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/50" />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          {/* Logo transparent */}
          <div className="mb-8 animate-float">
            <img
              src={restaurantInfo.logo}
              alt="Ya Habibi Logo"
              className="w-64 md:w-80 h-auto mx-auto drop-shadow-2xl"
            />
          </div>

          {/* Animated Welcome Text */}
          <div className="h-24 md:h-28 flex items-center justify-center mb-8">
            <div className={`transition-all duration-500 ${isAnimating ? 'opacity-0 transform translate-y-4' : 'opacity-100 transform translate-y-0'}`}>
              <p className="text-lg text-[#E8D4BE] mb-2 font-medium">
                {welcomeTexts[currentWelcome].lang}
              </p>
              <h1 className="font-serif text-4xl md:text-6xl font-bold text-white">
                {welcomeTexts[currentWelcome].text}
              </h1>
            </div>
          </div>

          {/* Welcome dots indicator */}
          <div className="flex justify-center gap-2 mb-10">
            {welcomeTexts.slice(0, 8).map((_, index) => (
              <span
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentWelcome % 8 ? 'bg-[#D4A853] w-6' : 'bg-white/40'
                }`}
              />
            ))}
          </div>

          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
            {t('home', 'subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/menu"
              className="bg-[#D4A853] text-[#0f172a] px-8 py-4 rounded-md font-semibold hover:bg-[#E8D4BE] transition-all duration-200 hover:-translate-y-1 shadow-lg flex items-center justify-center gap-2"
            >
              {t('home', 'discoverMenu')}
              <ChevronRight size={20} />
            </Link>
            <Link
              to="/reservation"
              className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-md font-semibold hover:bg-white hover:text-[#0f172a] transition-all duration-200 hover:-translate-y-1"
            >
              {t('home', 'bookTable')}
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Utensils,
                title: t('home', 'homemade'),
                description: t('home', 'homemadeDesc'),
              },
              {
                icon: Users,
                title: t('home', 'ambiance'),
                description: t('home', 'ambianceDesc'),
              },
              {
                icon: Clock,
                title: t('nav', 'topBarHours').split(' - ')[0],
                description: t('nav', 'topBarHours').split(' - ').slice(1).join(' - '),
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-[#F5E6D3]/50 p-8 rounded-xl text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-[#E8D4BE] rounded-full flex items-center justify-center mx-auto mb-6">
                  <feature.icon size={28} className="text-[#8B4513]" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-[#0f172a] mb-4">
                  {feature.title}
                </h3>
                <p className="text-[#64748b] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery - Nos Plats */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#0f172a] mb-4">
              {t('home', 'ourDishes')}
            </h2>
            <p className="text-lg text-[#64748b] max-w-2xl mx-auto">
              {t('home', 'dishesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {galleryItems.map((item, index) => (
              <div
                key={index}
                onClick={() => setLightboxIndex(index)}
                className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                  index === 0 || index === 5 ? 'md:col-span-2 md:row-span-2' : ''
                }`}
                data-testid={`gallery-item-${index}`}
              >
                <div className={`${index === 0 || index === 5 ? 'aspect-square' : 'aspect-[4/3]'} overflow-hidden`}>
                  <img
                    src={item.src}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <div>
                    <p className="text-white font-semibold text-sm md:text-base">{item.name}</p>
                    {item.price && (
                      <p className="text-[#D4A853] font-bold text-sm">CHF {item.price}</p>
                    )}
                  </div>
                  <Search size={20} className="text-white/80 absolute top-4 right-4" />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-8 py-4 rounded-md font-semibold hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-1 shadow-lg"
              data-testid="view-full-menu-btn"
            >
              {t('home', 'viewFullMenu')}
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>

        {/* Lightbox Modal */}
        {lightboxIndex !== null && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightboxIndex(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setLightboxIndex(null)}
              className="absolute top-4 right-4 text-white/80 hover:text-white z-50 p-2"
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + galleryItems.length) % galleryItems.length); }}
              className="absolute left-4 text-white/80 hover:text-white z-50 p-2"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={36} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % galleryItems.length); }}
              className="absolute right-4 text-white/80 hover:text-white z-50 p-2"
              data-testid="lightbox-next"
            >
              <ChevronRight size={36} />
            </button>
            <div className="max-w-4xl max-h-[85vh] relative" onClick={(e) => e.stopPropagation()}>
              <img
                src={galleryItems[lightboxIndex].src}
                alt={galleryItems[lightboxIndex].name}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <div className="text-center mt-4">
                <p className="text-white font-serif text-xl">{galleryItems[lightboxIndex].name}</p>
                {galleryItems[lightboxIndex].price && (
                  <p className="text-[#D4A853] font-bold">CHF {galleryItems[lightboxIndex].price}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 bg-[#F5E6D3]" data-testid="google-reviews-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#0f172a] mb-4">
            {t('home', 'whatClientsSay')}
          </h2>
          <p className="text-lg text-[#0f172a]/70 mb-12 max-w-xl mx-auto">
            {t('home', 'clientsSubtitle')}
          </p>

          {/* Google Rating Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 max-w-lg mx-auto mb-10">
            {/* Google Logo */}
            <div className="flex justify-center mb-6">
              <svg viewBox="0 0 272 92" width="120" height="40" xmlns="http://www.w3.org/2000/svg">
                <path d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" fill="#EA4335"/>
                <path d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" fill="#FBBC05"/>
                <path d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z" fill="#4285F4"/>
                <path d="M225 3v65h-9.5V3h9.5z" fill="#34A853"/>
                <path d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z" fill="#EA4335"/>
                <path d="M35.29 41.19V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49-.01z" fill="#4285F4"/>
              </svg>
            </div>

            {/* Stars & Rating */}
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="font-serif text-5xl font-bold text-[#0f172a]">{reviewData.rating}</span>
              <div className="flex flex-col items-start">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={22} className="fill-[#FBBC05] text-[#FBBC05]" />
                  ))}
                </div>
                <span className="text-[#64748b] text-sm mt-1">{reviewData.count} {t('home', 'googleReviews')}</span>
              </div>
            </div>

            {/* View Reviews Link */}
            <a
              href="https://share.google/ubKgGGwSxy3rwUWX6"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#4285F4] hover:text-[#1a73e8] font-medium transition-colors mt-2"
              data-testid="view-google-reviews-link"
            >
              {t('home', 'viewAllReviews')}
              <ExternalLink size={16} />
            </a>
          </div>

          {/* Write Review Button */}
          <a
            href="https://www.google.com/maps/place/Ya+Habibi!+Grill+-+Restaurant+Libanais/@46.208584,6.144274,17z/data=!4m2!3m1!1s0x478c655a5dab7501:0x9969c1c8b245fedf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#0f172a] text-white px-8 py-4 rounded-md font-semibold hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-1 shadow-lg"
            data-testid="leave-google-review-btn"
          >
            <PenLine size={20} />
            {t('home', 'leaveReview')}
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0f172a] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="Background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
            Prêt à découvrir nos saveurs ?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Réservez votre table dès maintenant et laissez-vous transporter par les arômes du Liban
          </p>
          <Link
            to="/reservation"
            className="inline-flex items-center gap-2 bg-[#F5E6D3] text-[#0f172a] px-10 py-5 rounded-md font-bold text-lg hover:bg-[#E8D4BE] transition-all duration-200 hover:-translate-y-1 shadow-xl"
          >
            Réserver maintenant
            <ChevronRight size={24} />
          </Link>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-6 text-center">
            Le meilleur restaurant libanais à Genève
          </h2>
          <div className="text-[#64748b] space-y-4 leading-relaxed">
            <p>
              <strong>Ya Habibi! Grill</strong> est un restaurant libanais authentique situé au coeur de Genève, dans le quartier des Pâquis. Notre cuisine libanaise est préparée avec des ingrédients frais et des recettes traditionnelles transmises de génération en génération. Des mezzés froids comme le houmous, le moutabbal et le tabboulé, aux grillades sur charbon comme le mix grill, le chich taouk et les kefta — chaque plat est un voyage au Liban.
            </p>
            <p>
              Reconnu comme l'un des <strong>meilleurs restaurants libanais de Genève</strong>, Ya Habibi! vous accueille 7 jours sur 7 dans une ambiance chaleureuse et conviviale. Notre carte de vins libanais (Château Kefraya, Château Ksara, Ixsir) accompagne parfaitement nos spécialités.
            </p>
            <h3 className="font-serif text-xl font-bold text-[#0f172a] pt-4">Best Lebanese Restaurant in Geneva</h3>
            <p>
              Looking for the <strong>best Lebanese restaurant in Geneva</strong>? Ya Habibi! Grill offers authentic Lebanese cuisine in the heart of Geneva's Pâquis district. From traditional mezze platters and charcoal grills to homemade falafel and hummus — experience the true flavors of Lebanon. Our restaurant is open 7 days a week, perfect for lunch, dinner, or a late-night meal with friends and family.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
